import { useState, useEffect } from 'react';
import { ShippingMethodSectionProps } from '../../types';
import { RadioCard } from './RadioCard';

/**
 * Shipping method selection section for checkout.
 * Displays available shipping rates with carrier info, delivery estimates, and pricing.
 * Only renders when shipping rates are available (loading/error handled by parent).
 */
export function ShippingMethodSection({
  shippingRates,
  selectedRate,
  onRateChange,
}: ShippingMethodSectionProps) {
  // Start collapsed if a rate is already selected (e.g. on page refresh)
  const [isCollapsed, setIsCollapsed] = useState(!!selectedRate);

  // Re-expand whenever a fresh set of rates arrives (new address selected)
  useEffect(() => {
    setIsCollapsed(false);
  }, [shippingRates]);

  const summary = selectedRate
    ? `${selectedRate.displayName} — $${selectedRate.chargePrice.toFixed(2)}`
    : '';

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <button
        type="button"
        onClick={() => setIsCollapsed((v) => !v)}
        className="w-full flex items-center justify-between text-left"
        aria-expanded={!isCollapsed}
      >
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Shipping Method</h2>
          {isCollapsed && summary && (
            <div className="flex items-center gap-1.5 mt-0.5">
              <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-gray-500">{summary}</p>
            </div>
          )}
        </div>
        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${isCollapsed ? '' : 'rotate-180'}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <div className={`grid transition-[grid-template-rows] duration-500 ease-in-out ${isCollapsed ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]'}`}>
        <div className="overflow-hidden">
          <div className="mt-4 space-y-3">
            {shippingRates.map((rate) => (
              <RadioCard
                key={rate.serviceType}
                name="shippingRate"
                value={rate.serviceType}
                checked={selectedRate?.serviceType === rate.serviceType}
                onChange={() => {
                  onRateChange(rate);
                  setTimeout(() => setIsCollapsed(true), 400);
                }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-gray-900">{rate.displayName}</div>
                    {rate.estimatedDeliveryText && (
                      <div className="text-sm text-gray-600">{rate.estimatedDeliveryText}</div>
                    )}
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-gray-900">
                      ${rate.chargePrice.toFixed(2)}
                    </div>
                    {selectedRate?.serviceType === rate.serviceType && (
                      <svg
                        className="w-6 h-6 text-blue-600 ml-auto mt-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                </div>
              </RadioCard>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
